import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import { duelService } from '../../services/socialService';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Quick action modal for duel invites
 * Shows pending duel invites with accept/decline actions
 */
const DuelInviteQuickActionModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [actionFeedback, setActionFeedback] = useState({});

  // Fetch pending duel invites
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchDuelInvites();
    }
  }, [isOpen, user?.id]);

  const fetchDuelInvites = async () => {
    setLoading(true);
    try {
      // Get notifications of type 'duel_invite' that are unread
      const notifications = await notificationService?.getNotifications(user?.id, {
        type: 'duel_invite',
        status: 'unread'
      });

      // Filter for pending invites with metadata
      const pendingInvites = notifications?.filter(
        n => n?.metadata?.duel_id && !actionFeedback?.[n?.id]
      ) || [];

      setInvites(pendingInvites);
    } catch (error) {
      console.error('Error fetching duel invites:', error);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invite) => {
    const duelId = invite?.metadata?.duel_id;
    if (!duelId || processingId) return;

    setProcessingId(invite?.id);
    try {
      // Accept the duel
      await duelService?.acceptDuel(duelId, user?.id);
      
      // Mark notification as read
      await notificationService?.markAsRead(invite?.id);
      
      // Show success feedback
      setActionFeedback(prev => ({
        ...prev,
        [invite?.id]: { type: 'accepted', message: '¡Duelo aceptado!' }
      }));

      // Remove from list after animation
      setTimeout(() => {
        setInvites(prev => prev?.filter(i => i?.id !== invite?.id));
        setActionFeedback(prev => {
          const updated = { ...prev };
          delete updated?.[invite?.id];
          return updated;
        });
      }, 1500);
    } catch (error) {
      console.error('Error accepting duel:', error);
      setActionFeedback(prev => ({
        ...prev,
        [invite?.id]: { type: 'error', message: 'Error al aceptar' }
      }));
      setTimeout(() => {
        setActionFeedback(prev => {
          const updated = { ...prev };
          delete updated?.[invite?.id];
          return updated;
        });
      }, 2000);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (invite) => {
    const duelId = invite?.metadata?.duel_id;
    if (!duelId || processingId) return;

    setProcessingId(invite?.id);
    try {
      // Cancel the duel
      await duelService?.cancelDuel(duelId, user?.id);
      
      // Mark notification as read
      await notificationService?.markAsRead(invite?.id);
      
      // Show success feedback
      setActionFeedback(prev => ({
        ...prev,
        [invite?.id]: { type: 'declined', message: 'Duelo rechazado' }
      }));

      // Remove from list after animation
      setTimeout(() => {
        setInvites(prev => prev?.filter(i => i?.id !== invite?.id));
        setActionFeedback(prev => {
          const updated = { ...prev };
          delete updated?.[invite?.id];
          return updated;
        });
      }, 1500);
    } catch (error) {
      console.error('Error declining duel:', error);
      setActionFeedback(prev => ({
        ...prev,
        [invite?.id]: { type: 'error', message: 'Error al rechazar' }
      }));
      setTimeout(() => {
        setActionFeedback(prev => {
          const updated = { ...prev };
          delete updated?.[invite?.id];
          return updated;
        });
      }, 2000);
    } finally {
      setProcessingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/20">
                <Icon name="Swords" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Invitaciones de Duelo</h2>
                <p className="text-xs text-muted-foreground">Responde rápidamente</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              aria-label="Cerrar"
            >
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(80vh-80px)] p-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full"
                />
                <p className="text-sm text-muted-foreground">Cargando invitaciones...</p>
              </div>
            ) : invites?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted/30">
                  <Icon name="CheckCircle" size={32} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">Sin invitaciones pendientes</p>
                <p className="text-xs text-muted-foreground text-center">No tienes duelos por aceptar en este momento</p>
              </div>
            ) : (
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {invites?.map((invite) => {
                    const feedback = actionFeedback?.[invite?.id];
                    const isProcessing = processingId === invite?.id;

                    return (
                      <motion.div
                        key={invite?.id}
                        layout
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20, height: 0, marginBottom: 0 }}
                        transition={{ duration: 0.3 }}
                        className="relative bg-gradient-to-br from-card to-muted/20 border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                      >
                        {/* Feedback Overlay */}
                        <AnimatePresence>
                          {feedback && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className={`absolute inset-0 flex items-center justify-center rounded-xl z-10 ${
                                feedback?.type === 'accepted' ? 'bg-success/95' :
                                feedback?.type === 'declined'? 'bg-muted/95' : 'bg-error/95'
                              }`}
                            >
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", damping: 15 }}
                                className="flex flex-col items-center space-y-2"
                              >
                                <Icon 
                                  name={feedback?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} 
                                  size={40} 
                                  className="text-white" 
                                />
                                <p className="text-white font-bold text-lg">{feedback?.message}</p>
                              </motion.div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {/* Invite Content */}
                        <div className="space-y-3">
                          {/* Title */}
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                              <Icon name="Trophy" size={20} className="text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-foreground text-sm line-clamp-1">
                                {invite?.title || 'Nuevo Duelo'}
                              </h3>
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                {invite?.message || 'Te han retado a un duelo'}
                              </p>
                            </div>
                          </div>

                          {/* Metadata */}
                          {invite?.metadata && (
                            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                              {invite?.metadata?.activity_type && (
                                <div className="flex items-center space-x-1">
                                  <Icon name="Activity" size={12} />
                                  <span className="capitalize">{invite?.metadata?.activity_type}</span>
                                </div>
                              )}
                              {invite?.metadata?.target_value && (
                                <div className="flex items-center space-x-1">
                                  <Icon name="Target" size={12} />
                                  <span>{invite?.metadata?.target_value} {invite?.metadata?.unit || ''}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 pt-2">
                            <Button
                              variant="solid"
                              size="sm"
                              onClick={() => handleAccept(invite)}
                              disabled={isProcessing || !!feedback}
                              className="flex-1 bg-success hover:bg-success/90 text-white font-medium"
                            >
                              {isProcessing ? (
                                <>
                                  <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"
                                  />
                                  Aceptando...
                                </>
                              ) : (
                                <>
                                  <Icon name="Check" size={16} className="mr-1" />
                                  Aceptar
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDecline(invite)}
                              disabled={isProcessing || !!feedback}
                              className="flex-1 border-error text-error hover:bg-error/10 font-medium"
                            >
                              {isProcessing ? (
                                'Procesando...'
                              ) : (
                                <>
                                  <Icon name="X" size={16} className="mr-1" />
                                  Rechazar
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Footer */}
          {invites?.length > 0 && (
            <div className="border-t border-border p-3 bg-muted/30">
              <p className="text-xs text-center text-muted-foreground">
                {invites?.length} {invites?.length === 1 ? 'invitación pendiente' : 'invitaciones pendientes'}
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DuelInviteQuickActionModal;